/**
 * Model-agnostic AI dispatcher.
 *
 * After Phase I (docs/pricing/line-tier-integration-plan.md), a single
 * server-side Cloud Function (`vertexProxy`) dispatches BOTH Gemini and
 * Claude models — Gemini via the VertexAI SDK on `asia-south1`, Claude via
 * `@anthropic-ai/vertex-sdk` on the `global` endpoint. The client no longer
 * needs a separate Anthropic client or payload flattening; it just calls
 * `generateContentWithFallback` with the tier-selected model id.
 *
 * The `dispatchAiCall` wrapper is kept for two reasons:
 *   1. It hands aiService a single import ("dispatchAiCall") that will not
 *      change if we ever add another provider path in the future.
 *   2. It normalizes usage metadata into an `AiTokenUsage` regardless of
 *      which underlying model served the call — callers ledger against a
 *      single shape.
 */

import {
  generateContentWithFallback,
  type GeminiGenerateResult,
  type GeminiPayload,
  type GeminiUsageMetadata,
} from './geminiClient'
import { normalizeGeminiUsage, type AiTokenUsage } from '../utils/aiBilling'
import type { AiModelId } from '../types/ai'
import type { GenerationConfig } from '@google/generative-ai'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import { getModelSpec } from '../config/aiModelRegistry'

export interface AiDispatchRequest {
  model: AiModelId
  payload: GeminiPayload
  generationConfig?: GenerationConfig
  useGoogleSearch?: boolean
  signal?: AbortSignal
}

export interface AiDispatchResult {
  text: string
  /** Which pathway served — always 'studio' or 'vertex'. Anthropic responses
   *  come back tagged 'vertex' because they travel through vertexProxy. */
  source: 'studio' | 'vertex'
  usage: AiTokenUsage
  usageMetadata?: GeminiUsageMetadata
  modelUsed: AiModelId
}

export const dispatchAiCall = async (request: AiDispatchRequest): Promise<AiDispatchResult> => {
  // === [AI-TIER-DEBUG] outbound dispatch — gated on ENABLE_ALL_FEATURES_FOR_TESTING.
  // See docs/pricing/line-tier-integration-plan.md Phase K for removal checklist. ===
  const debugStartAt = testingOverrideAllowsAllFeatures() ? performance.now() : 0
  if (testingOverrideAllowsAllFeatures()) {
    const spec = getModelSpec(request.model)
    console.log(
      `[AI-TIER-DEBUG] dispatch.out · model=${request.model} · provider=${spec?.provider ?? 'unknown'} · region=${spec?.vertexLocation ?? 'unknown'}`,
    )
  }
  // === /AI-TIER-DEBUG ===

  const result: GeminiGenerateResult = await generateContentWithFallback({
    model: request.model,
    payload: request.payload,
    generationConfig: request.generationConfig,
    useGoogleSearch: request.useGoogleSearch,
    signal: request.signal,
  })

  // === [AI-TIER-DEBUG] inbound result — gated. Same removal checklist. ===
  if (testingOverrideAllowsAllFeatures()) {
    const latencyMs = Math.round(performance.now() - debugStartAt)
    console.log(
      `[AI-TIER-DEBUG] dispatch.in  · model=${request.model} · source=${result.source} · latency=${latencyMs}ms · outputChars=${result.text.length}`,
    )
  }
  // === /AI-TIER-DEBUG ===

  return {
    text: result.text,
    source: result.source,
    usage: normalizeGeminiUsage(
      result.usageMetadata,
      typeof request.payload === 'string' ? request.payload : '',
      result.text,
    ),
    usageMetadata: result.usageMetadata,
    modelUsed: request.model,
  }
}
